// src/pages/WorkCertifications.js
import React, { useEffect, useState } from "react";
import axios from "axios";

const API_WRK = process.env.REACT_APP_API_WRK;
// e.g. http://localhost:5000 from http://localhost:5000/api/worker
const API_HOST = API_WRK?.replace(/\/api\/worker\/?$/, "") || window.location.origin;

const WorkCertifications = () => {
  const [services, setServices] = useState([]);
  const [fileMap, setFileMap] = useState({});
  const [uploading, setUploading] = useState({});
  const workerId = localStorage.getItem("workerId");

  const fetchCerts = async () => {
    if (!workerId) return;
    try {
      const res = await axios.get(`${API_WRK}/profile/${workerId}`);
      setServices(res.data?.services || []);
    } catch (err) {
      console.error("Failed to load certifications:", err);
    }
  };

  useEffect(() => {
    fetchCerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId]);

  const handleFileChange = (e, serviceName) => {
    setFileMap((prev) => ({
      ...prev,
      [serviceName]: e.target.files?.[0] || null,
    }));
  };

  const handleUpload = async (serviceName) => {
    if (!fileMap[serviceName]) return alert("Please select a file.");

    setUploading((prev) => ({ ...prev, [serviceName]: true }));

    const formData = new FormData();
    formData.append("certificate", fileMap[serviceName]);

    try {
      await axios.post(
        `${API_WRK}/upload-certificate/${workerId}/${encodeURIComponent(serviceName)}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      alert("✅ Certificate uploaded successfully!");
      setFileMap((prev) => ({ ...prev, [serviceName]: null }));
      await fetchCerts(); // refresh after upload
    } catch (err) {
      console.error("❌ Upload error:", err);
      alert(err?.response?.data?.message || "❌ Upload failed");
    } finally {
      setUploading((prev) => ({ ...prev, [serviceName]: false }));
    }
  };

  const getStatus = (status) => {
    const safe = status?.toLowerCase();
    if (safe === "approved")
      return <span className="text-green-600 font-medium text-sm">✅ Approved</span>;
    if (safe === "rejected")
      return <span className="text-red-500 font-medium text-sm">❌ Rejected</span>;
    return <span className="text-yellow-600 font-medium text-sm">⏳ Pending</span>;
  };

  const fullUrl = (url) => (url?.startsWith("http") ? url : `${API_HOST}${url || ""}`);

  return (
    <div className="min-h-screen p-4 bg-gray-50 text-gray-800">
      <h1 className="text-xl font-bold mb-6 text-center text-black">📄 Work Certifications</h1>

      {services.length === 0 ? (
        <p className="text-center text-sm text-gray-500">No certifications uploaded yet.</p>
      ) : (
        <div className="space-y-4">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-lg p-4 shadow-sm border border-gray-200"
            >
              <p className="font-semibold text-base">🛠️ {service.name}</p>
              <p className="text-sm text-gray-600 mt-1">
                Tools: <span className="font-medium">{service.hasTools ? "Yes" : "No"}</span>
              </p>
              {service.name === "Moving" && (
                <p className="text-sm text-gray-600">
                  Truck: <span className="font-medium">{service.hasTruck ? "Yes" : "No"}</span>
                </p>
              )}

              {/* Certification Section */}
              <p className="text-sm text-gray-600 mt-1">
                Certification:{" "}
                {service.certUrl ? (
                  <a
                    href={fullUrl(service.certUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    View Certificate
                  </a>
                ) : (
                  <span className="text-gray-500">Not Uploaded</span>
                )}
              </p>

              {/* Upload if not approved */}
              {service.certStatus !== "approved" && (
                <div className="mt-2">
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, service.name)}
                    className="block w-full mt-1 mb-2 text-sm"
                  />
                  <button
                    onClick={() => handleUpload(service.name)}
                    disabled={uploading[service.name]}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-1 rounded shadow-md"
                  >
                    {uploading[service.name] ? "Uploading..." : "Upload Certificate"}
                  </button>
                </div>
              )}

              {/* Status always shown */}
              <div className="mt-2">{getStatus(service.certStatus)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkCertifications;
